import { DeepFilterNet3Core } from 'deepfilternet3-noise-filter';
const Logger = require('../../Logger').default;

const logger = new Logger('DeepFilterNoiseCancellationTrack');

// Assets are self-hosted under public/audio-worklet/v3. The installed package's
// internal getAssetUrls() hardcodes its own path prefix (currently "v3" for 1.3.0,
// undocumented and inconsistent with the package README), so we don't rely on it -
// see below where we override AssetLoader#getAssetUrls to always point at our own
// known-good local files instead of whatever prefix the library currently hardcodes.
//
// IMPORTANT: the wasm binary and the JS "glue" code bundled inside this package
// (wasm-bindgen output) are ABI-coupled to each other - they MUST come from the
// same build. Pairing this glue with a wasm binary from a different path/version
// (e.g. an older "v2" build) fails at WebAssembly.Instance() with an obscure
// "Import #0 'wbg': module is not an object or function" error. If this package
// is ever upgraded, re-check the CDN path its dist/index.js actually requests
// (grep node_modules/deepfilternet3-noise-filter/dist/index.js for "getAssetUrls")
// and re-fetch the matching wasm binary into public/audio-worklet/vN before
// updating ASSET_PATHS below - don't just repoint the path without re-downloading.
const ASSET_CDN_URL = `${window.location.origin}${process.env.PUBLIC_URL || ''}/audio-worklet`;
const ASSET_PATHS = {
	wasm  : 'v3/pkg/df_bg.wasm',
	model : 'v3/models/DeepFilterNet3_onnx.tar.gz'
};

export function isNoiseCancellationSupported()
{
	return (
		typeof window !== 'undefined' &&
		typeof (window.AudioContext || window.webkitAudioContext) === 'function' &&
		typeof window.AudioWorkletNode === 'function' &&
		typeof window.WebAssembly === 'object'
	);
}

/**
 * Wraps a raw microphone MediaStreamTrack with an AudioWorklet running the
 * DeepFilterNet3 model, exposing a filtered MediaStreamTrack to produce instead.
 * Owns (and stops) the raw input track once started.
 */
export class DeepFilterNoiseCancellationTrack
{
	#core;
	#audioContext;
	#sourceNode;
	#workletNode;
	#destinationNode;
	#sampleRate;

	constructor(inputTrack, { sampleRate = 48000, suppressionLevel = 50 } = {})
	{
		this.inputTrack = inputTrack;
		this.outputTrack = null;
		this.#sampleRate = sampleRate;
		this.#core = new DeepFilterNet3Core({
			sampleRate,
			noiseReductionLevel : suppressionLevel,
			assetConfig         : { cdnUrl: ASSET_CDN_URL }
		});

		// Pin the actual asset URLs ourselves instead of trusting the library's
		// internal (and, per above, unstable) path prefix.
		this.#core.assetLoader.getAssetUrls = () => ({
			wasm  : this.#core.assetLoader.getCdnUrl(ASSET_PATHS.wasm),
			model : this.#core.assetLoader.getCdnUrl(ASSET_PATHS.model)
		});
	}

	async start()
	{
		logger.debug('start() [trackId:"%s"]', this.inputTrack.id);

		try
		{
			const AudioContextClass = window.AudioContext || window.webkitAudioContext;

			this.#audioContext = new AudioContextClass({ sampleRate: this.#sampleRate });

			await this.#core.initialize();

			this.#workletNode = await this.#core.createAudioWorkletNode(this.#audioContext);

			this.#sourceNode = this.#audioContext.createMediaStreamSource(
				new MediaStream([ this.inputTrack ])
			);
			this.#destinationNode = this.#audioContext.createMediaStreamDestination();

			this.#sourceNode.connect(this.#workletNode).connect(this.#destinationNode);

			if (this.#audioContext.state === 'suspended')
			{
				await this.#audioContext.resume();
			}

			[ this.outputTrack ] = this.#destinationNode.stream.getAudioTracks();

			return this.outputTrack;
		}
		catch (error)
		{
			logger.error('start() failed %o', error);

			this.#sourceNode?.disconnect();
			this.#workletNode?.disconnect();

			if (this.#audioContext && this.#audioContext.state !== 'closed')
			{
				await this.#audioContext.close().catch(() => {});
			}

			throw error;
		}
	}

	setSuppressionLevel(level)
	{
		this.#core?.setSuppressionLevel(level);
	}

	setEnabled(enabled)
	{
		this.#core?.setNoiseSuppressionEnabled(enabled);
	}

	async stop()
	{
		logger.debug('stop() [trackId:"%s"]', this.inputTrack?.id);

		try
		{
			this.#sourceNode?.disconnect();
			this.#workletNode?.disconnect();
			this.#core?.destroy();

			this.outputTrack?.stop();
			this.inputTrack?.stop();

			if (this.#audioContext && this.#audioContext.state !== 'closed')
			{
				await this.#audioContext.close();
			}
		}
		catch (error)
		{
			logger.error('stop() %o', error);
		}
	}
}
